/**
 * Brochure Builder — master-admin cross-dealership rollup.
 *
 * Route: sidebar id `admin-brochure-network` (master-admin only).
 * Backend: GET /api/admin/brochure/network-summary?days=N
 *
 * The purpose is simple: master needs to eyeball, in one page,
 *   1) how many dealers are actually using Brochure Builder,
 *   2) which enabled dealers are stalled (paying, not shipping),
 *   3) where the volume is coming from.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Loader2, RefreshCw, FileText, AlertTriangle } from "lucide-react";

function shortDate(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function StatBox({ label, value, tone = "default", testid }) {
  const toneCls = {
    default: "border-gray-200",
    warn: "border-amber-200 bg-amber-50",
    good: "border-emerald-200 bg-emerald-50",
  }[tone] || "border-gray-200";
  return (
    <div className={`border rounded-md px-4 py-3 min-w-[110px] ${toneCls}`}
         data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="text-[22px] font-bold text-gray-900 font-mono leading-tight">
        {value}
      </div>
    </div>
  );
}

export default function BrochureNetworkSummaryPage({ onNavigate }) {
  const { apiFetch } = useAuth();
  const { error: toastErr } = useToast();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/admin/brochure/network-summary?days=${days}`,
        { method: "GET" });
      setData(r?.data || r);
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Could not load network summary.");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, days, toastErr]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const dealers = data?.dealers || [];

  return (
    <div className="p-6 space-y-5" data-testid="brochure-network-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText size={22} /> Brochure Builder — Network
          </h1>
          <p className="text-[13px] text-gray-500 mt-1 max-w-2xl">
            Rollup of Brochure Builder usage across every enabled dealership.
            Stalled dealers (enabled but zero brochures in-window) are flagged so
            you can drop them a note before they cancel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded border text-[11.5px] font-semibold transition-colors ${
                  days === d
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
                data-testid={`brochure-network-days-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
            data-testid="brochure-network-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-gray-500 text-[13px]"
             data-testid="brochure-network-loading">
          <Loader2 className="animate-spin" size={14} /> Loading network summary…
        </div>
      ) : (
        <>
          {/* Totals strip */}
          <div className="flex flex-wrap gap-2" data-testid="brochure-network-totals">
            <StatBox label="Enabled dealers" value={totals.dealers_enabled || 0}
                     testid="brochure-network-stat-enabled" />
            <StatBox label="Active" value={totals.dealers_active || 0} tone="good"
                     testid="brochure-network-stat-active" />
            <StatBox label="Stalled" value={totals.dealers_stalled || 0}
                     tone={(totals.dealers_stalled || 0) > 0 ? "warn" : "default"}
                     testid="brochure-network-stat-stalled" />
            <StatBox label="Brochures" value={totals.brochures_created || 0}
                     testid="brochure-network-stat-brochures" />
            <StatBox label="Opens" value={totals.opens || 0}
                     testid="brochure-network-stat-opens" />
            <StatBox label="Calls" value={totals.calls || 0}
                     testid="brochure-network-stat-calls" />
            <StatBox label="Texts" value={totals.texts || 0}
                     testid="brochure-network-stat-texts" />
            <StatBox label="Credit app" value={totals.credit_clicks || 0}
                     testid="brochure-network-stat-credit" />
          </div>

          {/* Dealer table */}
          <div className="bg-white rounded-lg border border-gray-200 p-5"
               data-testid="brochure-network-dealer-card">
            {dealers.length === 0 ? (
              <div className="text-[13px] text-gray-500 italic py-8 text-center border border-dashed border-gray-200 rounded-md"
                   data-testid="brochure-network-empty">
                No dealers with Brochure Builder activity in this window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]"
                       data-testid="brochure-network-dealer-table">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Dealer</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 text-right">Brochures</th>
                      <th className="py-2 pr-3 text-right">Active reps</th>
                      <th className="py-2 pr-3 text-right">Opens</th>
                      <th className="py-2 pr-3 text-right">Call</th>
                      <th className="py-2 pr-3 text-right">Text</th>
                      <th className="py-2 pr-3 text-right">Credit app</th>
                      <th className="py-2 pr-3">Last brochure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealers.map((d) => (
                      <tr key={d.business_id}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => onNavigate?.("admin-dealer-detail", d.business_id, "brochure")}
                          data-testid={`brochure-network-row-${d.business_id}`}>
                        <td className="py-2 pr-3">
                          <div className="font-semibold text-gray-900 truncate max-w-[240px]">
                            {d.dealer_name}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          {d.is_stalled ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                                  data-testid={`brochure-network-status-${d.business_id}`}>
                              <AlertTriangle size={10} /> Stalled
                            </span>
                          ) : d.enabled ? (
                            <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
                                  data-testid={`brochure-network-status-${d.business_id}`}>
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                                  data-testid={`brochure-network-status-${d.business_id}`}>
                              Off
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-right">{d.brochures_created}</td>
                        <td className="py-2 pr-3 font-mono text-right">{d.active_reps}</td>
                        <td className="py-2 pr-3 font-mono text-right">{d.opens}</td>
                        <td className="py-2 pr-3 font-mono text-right">{d.calls}</td>
                        <td className="py-2 pr-3 font-mono text-right">{d.texts}</td>
                        <td className="py-2 pr-3 font-mono text-right">{d.credit_clicks}</td>
                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                          {shortDate(d.last_brochure_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
